import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { driver } from 'driver.js';
import { DialogService } from '../dialog.service';
import { LabFacade } from '../../../views/labs/facade/lab.facade';
import { LabStatus } from '../../../views/labs/models/Labs';
import { ProjectFacade } from '../../../views/projects/facades/project.facade';
import { LoginService } from '../../../views/authentication/services/login.service';
import { UserMgmtFacade } from '../../../views/user-management/facade/user-mgmt.facade';
import { TourUiStateService } from './tour-ui-state.service';
import { tourStepsByRole, TourStep, StepView, TourPage } from './tour-steps';
import { tourRoleFor } from '../../utils/user-roles';
import { labSectionNavFor } from '../../utils/lab-scoped-nav';
import { LabSection } from '../../utils/lab-tab';
import {
  tourLabContext,
  isStepAvailableForContext,
  parseRouteLabProject,
  TourLabContext,
} from './tour-context';

type DriverObj = ReturnType<typeof driver>;

export const TOUR_WELCOME_MODAL_ID = 'tour_welcome_modal';
export const TOUR_DONE_MODAL_ID = 'tour_done_modal';

@Injectable({ providedIn: 'root' })
export class TourService {
  private readonly dialogService = inject(DialogService);
  private readonly router = inject(Router);
  private readonly labFacade = inject(LabFacade);
  private readonly projectFacade = inject(ProjectFacade);
  private readonly tourUi = inject(TourUiStateService);
  private readonly loginService = inject(LoginService);
  private readonly userMgmtFacade = inject(UserMgmtFacade);

  private driverObj: DriverObj | null = null;
  private activeSteps: TourStep[] = [];
  private firstLabId: number | null = null;
  private firstProjectLabId: number | null = null;
  private firstProjectId: number | null = null;
  private tourCtx: TourLabContext = {
    labId: null,
    labInactive: false,
    projectLabId: null,
    projectId: null,
  };
  private startFromView: StepView = null;
  private inPageHelpMode = false;

  // Whether first-run auto-launch should wait. Lab-scoped users land on the
  // lab-selection page before their dashboard; the tour (which introduces the
  // active lab and the lab-scoped menu) should not fire until they've picked a
  // lab. Also wait until the current user has loaded, so we know their role.
  shouldDeferAutoLaunch(): boolean {
    // Never auto-launch on the lab picker: its target elements don't exist and
    // the tour cannot navigate off it until a valid lab is chosen.
    if (this.router.url.startsWith('/select-lab')) return true;
    const me = this.userMgmtFacade.getMeUserSignal();
    if (!me) return true;
    if (this.tourRole() !== 'labScoped') return false;
    return this.labFacade.selectedLabSignal() == null;
  }

  maybeStartForFirstTimeUser(): void {
    this.loginService.getOrientationStatus().subscribe({
      next: (resp) => {
        if (!resp.has_seen_orientation) setTimeout(() => this.openWelcome(), 0);
      },
      // network/auth failure: stay silent rather than spam the welcome modal
      error: () => undefined,
    });
  }

  replay(): void {
    this.startFromView = null;
    setTimeout(() => this.openWelcome(), 0);
  }

  // page-scoped help: runs only the steps for the current route, no welcome, no done modal
  startPageHelp(): void {
    const page = this.pageForUrl(this.router.url);
    if (!page) return;

    this.setPageHelpContext();
    // Same availability filter as the full tour: an inactive lab or a lab/
    // project the user has no data for would otherwise float unanchored popovers.
    const steps = this.roleSteps().filter(
      (s) => s.page === page && isStepAvailableForContext(s.view, this.tourCtx)
    );
    if (!steps.length) return;

    this.inPageHelpMode = true;
    this.activeSteps = steps;
    this.startFromView = null;
    this.driverObj?.destroy();
    this.driverObj = this.buildDriver(steps);
    void this.gotoStepView(steps[0].view).then(() => this.driverObj?.drive(0));
  }

  hasPageHelp(url: string): boolean {
    const page = this.pageForUrl(url);
    return page != null && this.roleSteps().some((s) => s.page === page);
  }

  // The layout the user is currently in. A platform admin who has switched into
  // one of their own labs tours the lab-scoped experience they can actually see.
  private tourRole() {
    return tourRoleFor(this.userMgmtFacade.getMeUserSignal(), this.labFacade.selectedLabSignal()?.id);
  }

  // the step set for the current user's layout (lab-scoped / data analyst / admin)
  private roleSteps(): TourStep[] {
    return tourStepsByRole[this.tourRole()];
  }

  startTour(): void {
    this.dialogService.close(TOUR_WELCOME_MODAL_ID);
    this.markSeen();
    void this.runTour();
  }

  private async runTour(): Promise<void> {
    // The tour's anchors live on the dashboard and lab pages, not the lab
    // picker. If we're on /select-lab, move to the dashboard first; if we can't
    // get there (no valid lab selected), abort rather than float unanchored
    // popovers over the picker.
    if (this.router.url.startsWith('/select-lab')) {
      await this.navigate(['/dashboard']);
      if (this.router.url.startsWith('/select-lab')) return;
    }

    this.activeSteps = this.buildSteps();
    if (!this.activeSteps.length) return;

    this.inPageHelpMode = false;
    this.driverObj?.destroy();
    this.driverObj = this.buildDriver(this.activeSteps);

    const startIdx = this.startFromView
      ? Math.max(
          0,
          this.activeSteps.findIndex((s) => s.view === this.startFromView)
        )
      : 0;
    await this.gotoStepView(this.activeSteps[startIdx].view);
    this.driverObj?.drive(startIdx);
    this.startFromView = null;
  }

  private buildDriver(steps: TourStep[]): DriverObj {
    return driver({
      showProgress: true,
      progressText: 'Step {{current}} of {{total}}',
      showButtons: ['next', 'previous', 'close'],
      nextBtnText: 'Next →',
      prevBtnText: '← Back',
      doneBtnText: 'Done',
      allowClose: true,
      overlayOpacity: 0.45,
      smoothScroll: true,
      steps,
      onPopoverRender: (popover) => {
        if (!popover.footer || popover.footer.querySelector('.tour-skip-btn')) return;
        const skip = document.createElement('button');
        skip.type = 'button';
        skip.textContent = this.inPageHelpMode ? 'Close' : 'Skip tour';
        skip.className = 'tour-skip-btn';
        skip.addEventListener('click', () => this.driverObj?.destroy());
        popover.footer.appendChild(skip);
      },
      onNextClick: () => {
        void this.advance('next');
      },
      onPrevClick: () => {
        void this.advance('prev');
      },
      onDestroyed: () => {
        this.tourUi.reset();
        const wasPageHelp = this.inPageHelpMode;
        this.inPageHelpMode = false;
        if (!wasPageHelp) this.dialogService.open(TOUR_DONE_MODAL_ID);
      },
    });
  }

  skipTour(): void {
    this.dialogService.close(TOUR_WELCOME_MODAL_ID);
    this.markSeen();
  }

  private async advance(direction: 'next' | 'prev'): Promise<void> {
    if (!this.driverObj) return;
    const current = this.driverObj.getActiveIndex() ?? 0;
    const targetIdx = direction === 'next' ? current + 1 : current - 1;

    if (targetIdx < 0 || targetIdx >= this.activeSteps.length) {
      // boundary: let driver.js close the tour on its built-in "Done"
      if (direction === 'next') this.driverObj.moveNext();
      else this.driverObj.movePrevious();
      return;
    }

    await this.gotoStepView(this.activeSteps[targetIdx].view);
    if (direction === 'next') this.driverObj.moveNext();
    else this.driverObj.movePrevious();
  }

  // Resolve which lab/project the deep-feature steps deep-link into. Lab-scoped
  // users tour their selected lab; admins/analysts tour their first lab/project.
  private computeTourContext(): void {
    const role = this.tourRole();
    const labs = this.labFacade
      .labListSignal()
      .map((lab) => ({ id: lab.id, inactive: lab.status === LabStatus.INACTIVE }));
    const ctx = tourLabContext(
      role,
      labs,
      this.projectFacade.projectListSignal(),
      this.labFacade.selectedLabSignal()?.id ?? null
    );
    this.tourCtx = ctx;
    this.firstLabId = ctx.labId;
    this.firstProjectLabId = ctx.projectLabId;
    this.firstProjectId = ctx.projectId;
  }

  // Page-scoped help deep-links into the lab/project the user is currently
  // viewing (parsed from the route), so "Start from here" stays on that lab
  // rather than jumping to the tour's default resolved lab.
  private setPageHelpContext(): void {
    const { labId, projectId } = parseRouteLabProject(this.router.url);
    const lab = labId != null ? this.labFacade.labListSignal().find((l) => l.id === labId) : undefined;
    this.tourCtx = {
      labId,
      labInactive: lab?.status === LabStatus.INACTIVE,
      projectLabId: projectId != null ? labId : null,
      projectId,
    };
    this.firstLabId = this.tourCtx.labId;
    this.firstProjectLabId = this.tourCtx.projectLabId;
    this.firstProjectId = this.tourCtx.projectId;
  }

  // pick the role's step set, then drop steps whose target won't render for
  // this context (no labs / inactive lab / no projects)
  private buildSteps(): TourStep[] {
    this.computeTourContext();
    return this.roleSteps().filter((step) => isStepAvailableForContext(step.view, this.tourCtx));
  }

  private async gotoStepView(view: StepView): Promise<void> {
    if (!view) return;

    const url = this.router.url.split('?')[0];

    this.tourUi.uploadSequencesDropdownForced.set(false);
    this.tourUi.bottomPanelForcedOpen.set(false);

    switch (view) {
      case 'dashboard':
        if (!url.startsWith('/dashboard')) await this.navigate(['/dashboard']);
        return;
      case 'labs':
        if (url !== '/labs') await this.navigate(['/labs']);
        return;
      case 'lab-detail':
        if (this.firstLabId !== null) {
          await this.navigate(['/labs', this.firstLabId]);
        }
        return;
      case 'lab-seq':
        // set before navigation so the component reads it on first effect run
        this.tourUi.uploadSequencesDropdownForced.set(true);
        await this.navigateToLabSection('sequences');
        return;
      case 'lab-seq-csv':
        await this.navigateToLabSection('sequences');
        return;
      case 'lab-roster':
        await this.navigateToLabSection('users');
        return;
      case 'projects':
        if (url !== '/projects') await this.navigate(['/projects']);
        return;
      case 'project-detail':
        if (this.firstProjectLabId !== null && this.firstProjectId !== null) {
          await this.navigate(['/labs', this.firstProjectLabId, 'project', this.firstProjectId]);
        }
        return;
      case 'project-detail-datasets':
        if (this.firstProjectLabId !== null && this.firstProjectId !== null) {
          await this.navigate(
            ['/labs', this.firstProjectLabId, 'project', this.firstProjectId],
            { tab: 'datasets' }
          );
        }
        return;
      case 'data-catalog':
      case 'data-catalog-search':
        if (url !== '/datasets') await this.navigate(['/datasets']);
        return;
      case 'user-guide':
        this.tourUi.bottomPanelForcedOpen.set(true);
        await new Promise((resolve) => setTimeout(resolve, 80));
        return;
    }
  }

  // A lab user has a page per section, a platform admin still has tabs, so
  // where a step lives depends on who is taking the tour.
  private async navigateToLabSection(section: LabSection): Promise<void> {
    if (this.firstLabId === null) return;
    const target = labSectionNavFor(
      this.userMgmtFacade.getMeUserSignal(),
      this.firstLabId,
      section,
      this.labFacade.selectedLabSignal()?.id
    );
    await this.navigate(target.commands, target.queryParams);
  }

  private async navigate(commands: unknown[], queryParams?: Record<string, string>): Promise<void> {
    await this.router.navigate(commands, queryParams ? { queryParams } : undefined);
    // wait one paint for the destination component to mount
    await new Promise((resolve) => setTimeout(resolve, 80));
  }

  private openWelcome(): void {
    this.dialogService.open(TOUR_WELCOME_MODAL_ID);
  }

  private pageForUrl(url: string): TourPage | null {
    const path = url.split('?')[0];
    if (/^\/labs\/\d+\/project\/\d+/.test(path)) return 'project-detail';
    if (/^\/labs\/\d+/.test(path)) return 'lab-detail';
    if (path === '/labs') return 'labs';
    if (path === '/projects') return 'projects';
    if (path === '/datasets') return 'data-catalog';
    if (path.startsWith('/dashboard')) return 'dashboard';
    return null;
  }

  // best-effort POST; if it fails the user will be re-prompted next login
  private markSeen(): void {
    this.loginService.markOrientationSeen().subscribe({ error: () => undefined });
  }
}
